/** @format */

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Button } from 'src/components';
// import {ArrowRight} from '@mui/icons-material';
import useAuth from 'src/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import {
  deleteUserProfilePicture,
  getNewsletterConfig,
  setUserData,
  updateNewsletterConfig,
  updateUserInfo,
  updateUserProfilePicture,
  uploadUserProfilePicture
} from 'src/slices/users';
import { getPublishedTopics } from 'src/slices/topics';
import { useDispatch } from 'src/store';
import Breadcrumb from 'src/components/Breadcrumb';
import { countriesMap } from 'src/utils/dataMap';
import { useSelector } from 'src/store';
import prettyBytes from 'pretty-bytes';
import {
  AdditionalInformationSection,
  CompanyAddressSection,
  ContactInformationSection,
  LanguageSection,
  NewsletterSection,
  ProfileCompletionSection,
  ProfileDataSection,
  ProfilePictureSection
} from 'src/components/personaldata';
import Page from 'src/components/Page';
import type { UserFormData } from 'src/types';
import {
  getFirstIncompleteProfileFieldSelector,
  getProfileCompletionPercentage
} from 'src/utils/profileCompletion';

type ProfileErrorMessage = Record<string, string | null>;

const PersonalData = props => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { error, userProfile, userLoading, userData, newsletterConfig } = useSelector(state => state.users);
  const {
    publishedTopics: newsletterTopicTree,
    publishedTopicsLoading: newsletterTopicsLoading,
    publishedTopicsError: newsletterTopicsError
  } = useSelector(state => state.topics);
  const [errorMessage, setErrorMessage] = useState<ProfileErrorMessage>({});
  const [userProfilePicture, setUserProfilePicture] = useState('');
  const { t, i18n } = useTranslation();
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [newsletterActive, setNewsletterActive] = useState(false);
  const [newsletterFrequency, setNewsletterFrequency] = useState('weekly');
  const [newsletterTopics, setNewsletterTopics] = useState<number[]>([]);
  const formData = (userData ?? {}) as UserFormData;

  const goBack = () => {
    navigate('/profile');
  };

  useEffect(() => {
    if (userProfile && userProfile.picture && !userProfilePicture) {
      setUserProfilePicture(userProfile.picture);
    }
    // eslint-disable-next-line
  }, [userProfile]);

  useEffect(() => {
    if (error?.error) {
      const errorMsg = error.error
        .replace('The mime type of the file is invalid', t('personalData.profile_errors.mime_1'))
        .replace('Allowed mime types are', t('personalData.profile_errors.mime_2'))
        .replace('An image for this user already exists.', t('personalData.profile_errors.already_exists'))
        .replace('Unable to save image.', t('personalData.profile_errors.unable_save'))
        .replace('No image uploaded.', t('personalData.profile_errors.no_image_uploaded'))
        .replace('The file was only partially uploaded.', t('personalData.profile_errors.was_partially_uploaded'))
        .replace('No file was uploaded.', t('personalData.profile_errors.no_file_uploaded'))
        .replace('The file could not be uploaded.', t('personalData.profile_errors.file_could_not_be_uploaded'));
      setErrorMessage(prev => ({ ...prev, avatar: errorMsg }));
      setUserProfilePicture('');
      if (inputFileRef.current) {
        inputFileRef.current.value = '';
      }
    }
    // eslint-disable-next-line
  }, [error]);

  useEffect(() => {
    if (userData && Object.prototype.hasOwnProperty.call(userData, 'newsletter')) {
      setNewsletterActive(Boolean((userData as UserFormData).newsletter));
    }
  }, [userData]);

  useEffect(() => {
    dispatch(getPublishedTopics());
  }, [dispatch]);

  useEffect(() => {
    if (user?.uuid) {
      dispatch(getNewsletterConfig(user.uuid));
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (!newsletterConfig) {
      return;
    }

    setNewsletterActive(Boolean(newsletterConfig?.active));
    if (typeof newsletterConfig?.frequency === 'string') {
      setNewsletterFrequency(newsletterConfig.frequency.toLowerCase());
    }

    const selectedTopics = Object.values(newsletterConfig?.topics || {}).filter(topicId => Number.isInteger(topicId));
    setNewsletterTopics(selectedTopics);
  }, [newsletterConfig]);

  const setData = e => {
    const { name, value } = e.target;
    dispatch(
      setUserData({
        ...formData,
        [name]: value
      })
    );
  };
  const setAddress = e => {
    const { name, value } = e.target;
    const address = { ...(formData.address ?? {}), [name]: value };
    dispatch(
      setUserData({
        ...formData,
        address
      })
    );
  };
  const saveProfileData = async () => {
    const { firstName, lastName, company, address = {} } = formData;
    setErrorMessage({
      ...errorMessage,
      firstName: firstName ? null : t('personalData.errorMessage.firstName'),
      lastName: lastName ? null : t('personalData.errorMessage.lastName'),
      company: company ? null : t('personalData.errorMessage.company'),
      phoneNumber: null,
      address: address.street && address.city ? null : t('personalData.errorMessage.address')
    });
    if (firstName && lastName && company && address.street && address.city) {
      setErrorMessage({ error: null });
      setSuccessMessage(null);
      await dispatch(updateUserInfo(userData));
      if (user?.uuid) {
        await dispatch(
          updateNewsletterConfig({
            uuid: user.uuid,
            payload: {
              active: newsletterActive,
              frequency: newsletterFrequency,
              topics: newsletterTopics
            }
          })
        );
      }
      setSuccessMessage(t('personalData.successMessage'));
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    } else {
      setSuccessMessage(null);
    }
  };

  const breadcrumbData = [
    {
      title: t('profile.title'),
      link: '/profile',
      options: [
        {
          title: t('profile.title'),
          link: '/profile'
        },
        {
          title: t('common.personalData'),
          link: '/personaldata'
        },
        {
          title: t('common.access'),
          link: '/access'
        }
      ]
    },
    {
      title: t('common.personalData')
    }
  ];

  const onChangeProFilePicture = async e => {
    setSuccessMessage(null);
    try {
      const [file] = e.target.files;
      if (file) {
        const { size } = file;
        if (size / (1024 * 1024) > 2) {
          const sizePretty = prettyBytes(size);
          setErrorMessage({
            ...errorMessage,
            avatar: t('personalData.profile_errors.maxSize', { fileSize: sizePretty, limit: '2 MB' })
          });
          return;
        }

        const picture = URL.createObjectURL(file);
        setUserProfilePicture(picture);
        e.target.value = null;

        if (userProfilePicture) await dispatch(deleteUserProfilePicture());

        const data = new FormData();
        data.append('file', file);

        await dispatch(uploadUserProfilePicture({ data }));
        setSuccessMessage(t('personalData.successMessage'));
        setErrorMessage({
          ...errorMessage,
          avatar: ''
        });
        await dispatch(updateUserProfilePicture());
      }
    } catch (error) {
      setErrorMessage({
        ...errorMessage,
        avatar: 'Something went wrong!'
      });
    }
  };

  const deleteUserProfile = async () => {
    await dispatch(deleteUserProfilePicture());
    if (inputFileRef.current) {
      inputFileRef.current.value = '';
    }
    setUserProfilePicture('');
  };

  const checkErrorMessage = () => {
    return Object.keys(errorMessage).some(key => {
      return errorMessage[key] !== null && errorMessage[key] !== '';
    });
  };

  const profileCompletionOptions = useMemo(
    () => ({ hasProfilePicture: Boolean(userProfilePicture) }),
    [userProfilePicture]
  );

  const profileCompletionPercentage = useMemo(
    () =>
      getProfileCompletionPercentage(
        userData as UserFormData | null | undefined,
        profileCompletionOptions
      ),
    [userData, profileCompletionOptions]
  );

  const goToFirstIncompleteField = () => {
    const selector = getFirstIncompleteProfileFieldSelector(
      userData as UserFormData | null | undefined,
      profileCompletionOptions
    );

    if (!selector) return;

    const targetField = document.querySelector(selector);

    if (!targetField) return;

    targetField.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });

    if (selector === '#profilePictureUploadBtn') {
      (targetField as HTMLInputElement).click();
      return;
    }

    (targetField as HTMLElement).focus();
  };

  return (
    <Page title={t('common.personalData')}>
      <section className="main_content profile">
        <Breadcrumb data={breadcrumbData} additionalClasses="container--narrow" />
        <div className="container container--narrow">
          <div className="personal-data-container">
            <h2 className="page_title">
              {t('personalData.greeting')} {user && user.firstName} {user && user.lastName}
              {user && user.firstName && '!'}
            </h2>

            {successMessage && (
              <div className="successMessage">
                <span>{successMessage}</span>
              </div>
            )}

            <ProfileCompletionSection
              completionPercentage={profileCompletionPercentage}
              onCompleteAction={goToFirstIncompleteField}
              t={t}
            />
            <ProfilePictureSection
              t={t}
              i18n={i18n}
              userLoading={userLoading}
              userProfile={userProfile}
              userProfilePicture={userProfilePicture}
              userData={userData}
              inputFileRef={inputFileRef}
              onDeleteProfilePicture={deleteUserProfile}
              onChangeProfilePicture={onChangeProFilePicture}
              avatarError={errorMessage.avatar}
            />
            <ProfileDataSection
              t={t}
              userData={userData}
              errorMessage={errorMessage}
              onSetData={setData}
            />
            <CompanyAddressSection
              t={t}
              userData={userData}
              errorMessage={errorMessage}
              countriesMap={countriesMap}
              onSetAddress={setAddress}
            />
            <ContactInformationSection
              t={t}
              userData={userData}
              onSetAddress={setAddress}
            />
            <AdditionalInformationSection t={t} userData={userData} onSetData={setData} />
            <LanguageSection t={t} userData={userData} user={user} onSetData={setData} />
            <NewsletterSection
              t={t}
              newsletterActive={newsletterActive}
              newsletterFrequency={newsletterFrequency}
              newsletterTopics={newsletterTopics}
              newsletterTopicTree={newsletterTopicTree}
              newsletterTopicsLoading={newsletterTopicsLoading}
              newsletterTopicsError={newsletterTopicsError}
              onNewsletterActiveChange={checked => {
                setNewsletterActive(checked);
                dispatch(
                  setUserData({
                    ...formData,
                    newsletter: checked
                  })
                );
              }}
              onNewsletterFrequencyChange={setNewsletterFrequency}
              onNewsletterTopicsChange={setNewsletterTopics}
            />
            {checkErrorMessage() && (
              <div className="errorMessage">
                <span>{t('personalData.errorMessage.overall')}</span>
              </div>
            )}
            <Grid>
              <div className="personal-data__footer-btn">
                <Button onClick={saveProfileData} label={t('common.button.save')} />
                <Button onClick={goBack} secondary label={t('personalData.Abortbtn')} />
              </div>
            </Grid>
          </div>
        </div>
      </section>
    </Page>
  );
};

export { PersonalData };
